import React, { useMemo } from 'react';
import { Card, Empty, Checkbox } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { UsageHistoryData } from '../../services/usageService';

interface UsageChartProps {
  historyData: UsageHistoryData[];
  selectedFeatures?: string[];
  onFeatureToggle?: (featureCode: string, checked: boolean) => void;
}

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

const UsageChart: React.FC<UsageChartProps> = ({ 
  historyData, 
  selectedFeatures,
  onFeatureToggle 
}) => {
  // Group data by month
  const chartData = useMemo(() => {
    const grouped = historyData.reduce((acc, item) => {
      if (!acc[item.month]) {
        acc[item.month] = { month: item.month };
      }
      acc[item.month][item.featureCode] = item.usageCount;
      return acc;
    }, {} as Record<string, any>);

    return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
  }, [historyData]);

  // Get unique features
  const features = useMemo(() => {
    const featureMap = new Map<string, string>();
    historyData.forEach(item => {
      if (!featureMap.has(item.featureCode)) {
        featureMap.set(item.featureCode, item.featureName);
      }
    });
    return Array.from(featureMap.entries()).map(([code, name], index) => ({
      code,
      name,
      color: COLORS[index % COLORS.length],
    }));
  }, [historyData]);

  const displayedFeatures = selectedFeatures 
    ? features.filter(f => selectedFeatures.includes(f.code))
    : features;

  if (historyData.length === 0) {
    return (
      <Card>
        <Empty description="Henüz kullanım geçmişi bulunmuyor" />
      </Card>
    );
  }

  // Format month for display (YYYY-MM-DD to MMM YYYY)
  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
  };

  return (
    <Card
      title="Son 6 Ay Kullanım Grafiği"
      extra={
        onFeatureToggle && features.length > 1 && (
          <div style={{ display: 'flex', gap: 12 }}>
            {features.map(feature => (
              <Checkbox
                key={feature.code}
                checked={!selectedFeatures || selectedFeatures.includes(feature.code)}
                onChange={(e) => onFeatureToggle(feature.code, e.target.checked)}
              >
                <span style={{ color: feature.color }}>{feature.name}</span>
              </Checkbox>
            ))}
          </div>
        )
      }
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            tickFormatter={formatMonth}
            style={{ fontSize: 12 }}
          />
          <YAxis style={{ fontSize: 12 }} />
          <Tooltip
            labelFormatter={formatMonth}
            contentStyle={{ borderRadius: 4 }}
          />
          <Legend />
          {displayedFeatures.map(feature => (
            <Line
              key={feature.code}
              type="monotone"
              dataKey={feature.code}
              name={feature.name}
              stroke={feature.color}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default UsageChart;

